import TicketDetails from 'components/tickets/TicketDetails';
import useApi from 'helpers/api';
import { CommunicationProvider } from 'hooks/useCommunication';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import TicketsService from 'services/Tickets.service';
import AssetInfo from './AssetInfo';
import ContactInfo from './ContactInfo';
import DattoDeviceDetails from './DattoDeviceDetails';
import PropertiesTicketForm from './PropertiesTicketForm';
import SlaInfo from './SlaInfo';
import TicketsPreviewHeader from './TicketsPreviewHeader';

const TicketsPreview = () => {
  const { id } = useParams();
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const navigate = useNavigate();
  const ticketDetails = useRef();
  const propertiesFormRef = useRef();

  const [ticket, setTicket] = useState({});

  const [sizeMainSide, setSizeMainSide] = useState(6);

  const [showLeftSide, setShowLeftSide] = useState(true);
  const [showRightSide, setShowRightSide] = useState(true);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getDetails();
  }, [id]);

  useEffect(() => {
    if (showLeftSide && showRightSide) {
      setSizeMainSide(6);
    } else if (showLeftSide || showRightSide) {
      setSizeMainSide(9);
    } else if (!showLeftSide && !showRightSide) {
      setSizeMainSide(12);
    }
  }, [showLeftSide, showRightSide]);

  const getDetails = async () => {
    setIsLoading(true);
    const response = await TicketsService.findById(id);
    if (response.userMessage) {
      showErrorNotification(response.userMessage);
      navigate('/tickets');
    } else {
      setTicket(response);
    }
    setIsLoading(false);
  };

  const editTicket = async body => {
    const response = await TicketsService.edit(ticket.id, body);
    if (response.status) {
      getDetails();
      ticketDetails.current.getNewLogs();
      showSuccessNotification('Ticket editado com sucesso');
    } else {
      showErrorNotification(response.userMessage);
    }
  };

  return (
    <CommunicationProvider>
      {isLoading ? (
        <Row className="g-2">
          <Col xl={12}>
            <TicketsPreviewHeader
              data={ticket}
              refreshData={() => getDetails()}
              refreshLogs={() => ticketDetails.current?.getNewLogs()}
              refreshFiles={() => ticketDetails.current?.getFiles()}
              refreshNotes={() => ticketDetails.current?.getNotes()}
              propertiesFormRef={propertiesFormRef}
              success={success => {
                if (success != null) {
                  showSuccessNotification(success);
                }
              }}
              error={error => {
                if (error != null) {
                  showErrorNotification(error);
                }
              }}
            />
          </Col>
          <Col xl={3}>
            <Skeleton
              height={224}
              className="mb-2"
              borderRadius={15}
              baseColor="#5E6E82"
            />
            <Skeleton height={484} borderRadius={15} baseColor="#5E6E82" />
          </Col>
          <Col xl={6}>
            <Skeleton height={480} borderRadius={15} baseColor="#5E6E82" />
          </Col>
          <Col xl={3} className="">
            <Skeleton
              height={308}
              className="mb-2"
              borderRadius={15}
              baseColor="#5E6E82"
            />
            <Skeleton height={109} borderRadius={15} baseColor="#5E6E82" />
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col xl={12}>
              <TicketsPreviewHeader
                data={ticket}
                refreshData={() => getDetails()}
                refreshLogs={() => ticketDetails.current.getNewLogs()}
                refreshFiles={() => ticketDetails.current.getFiles()}
                refreshNotes={() => ticketDetails.current.getNotes()}
                propertiesFormRef={propertiesFormRef}
                success={success => {
                  if (success != null) {
                    showSuccessNotification(success);
                  }
                }}
                error={error => {
                  if (error != null) {
                    showErrorNotification(error);
                  }
                }}
              />
            </Col>
            {showLeftSide && (
              <Col xxl={3} xl={3} className={`${showLeftSide && 'ps-3 pe-2'}`}>
                <Row className="gy-3 gx-0">
                  <Col xs={12}>
                    <ContactInfo
                      data={ticket}
                      refreshTicket={() => getDetails()}
                    />
                  </Col>
                  <Col md={6} xl={12}>
                    <PropertiesTicketForm
                      ref={propertiesFormRef}
                      data={ticket}
                      handleSubmit={editTicket}
                    />
                  </Col>
                </Row>
              </Col>
            )}

            <Col
              md={sizeMainSide}
              className={`px-0 ${!showLeftSide && 'ps-3'} ${
                !showRightSide && 'pe-3'
              }`}
            >
              <div className="position-sticky">
                <TicketDetails
                  data={ticket}
                  refreshData={() => getDetails()}
                  showLeftSide={showLeftSide}
                  showRightSide={showRightSide}
                  setShowLeftSide={setShowLeftSide}
                  setShowRightSide={setShowRightSide}
                  ref={ticketDetails}
                />
              </div>
            </Col>

            {showRightSide && (
              <Col xxl={3} xl={3} className={`${showLeftSide && 'ps-2 pe-3'}`}>
                <Row className="g-3">
                  <Col md={6} xl={12}>
                    <SlaInfo data={ticket} refreshData={() => getDetails()} />
                  </Col>
                  {ticket?.asset?.dattoDevice && (
                    <Col md={6} xl={12}>
                      <DattoDeviceDetails data={ticket?.asset?.dattoDevice} />
                    </Col>
                  )}
                  <Col md={6} xl={12}>
                    <AssetInfo data={ticket} refreshData={() => getDetails()} />
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </>
      )}
    </CommunicationProvider>
  );
};

export default TicketsPreview;
